import { useState } from 'react';
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import { MetricsDateSelector } from '../filters/MetricsDateSelector';
import { METRICS_DIMENSION_OPTIONS, METRICS_DIMENSION_VALUES, METRICS_FORECAST_HORIZON_OPTIONS, METRICS_PERIOD_OPTIONS, PlotScope, PlotType } from '../filters/metricsFilterOptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { isDemoUser } from '../../utils/demo';

/**
 * MetricsFilterBar options
 */
export const MetricsFilterBar = (props: {
    filterOptions: any,
    updateFilterOptions: any,
    filterDateOptions: any,
    setFilterDateOptions: any,
    refresh: any,
    isLoadingMetrics: boolean,
    user: any,
}) => {

    const handleCheckFilter = (checked: boolean, value: string, key: string = "dimension_filter_values") => {
        const filterVals = new Set([...props.filterOptions[key]]);
        if (checked) {
            filterVals.add(value);
        } else {
            filterVals.delete(value);
        }

        props.updateFilterOptions(key, Array.from(filterVals));
    }

    return (
        <>
            <Row className="metrics-sidebar-update" style={{marginTop: isDemoUser(props.user) ? "80px" : undefined}}>
                <Col md={12} className="d-grid gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {props.refresh()}}
                        disabled={props.isLoadingMetrics}
                    >
                        {props.isLoadingMetrics ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</> : "Update"}
                    </Button>
                </Col>
            </Row>
            <Row className="mb-4" style={{marginTop: '48px'}}>
                <Col md={12}>
                    <div className="metrics-control-title">Date Range</div>
                    <MetricsDateSelector
                        filterDateOptions={props.filterDateOptions}
                        setFilterDateOptions={props.setFilterDateOptions}
                    />
                </Col>
            </Row>
            <Row className="mt-2 mb-4">
                <Col md={12}>
                    <div className="metrics-control-title">Plot Type</div>
                    <ButtonGroup size='sm' style={{marginRight: '4px'}} className="d-flex">
                        <Button
                            variant={props.filterOptions["type"] === PlotType.TimeTrends ? "secondary" : "outline-secondary"}
                            onClick={() => props.updateFilterOptions("type", PlotType.TimeTrends)}
                        >
                            Time Trends
                        </Button>
                        <Button
                            variant={props.filterOptions["type"] === PlotType.HourOfDay ? "secondary" : "outline-secondary"}
                            onClick={() => props.updateFilterOptions("type", PlotType.HourOfDay)}
                        >
                            Hour of Day
                        </Button>
                        <Button
                            variant={props.filterOptions["type"] === PlotType.DayOfWeek ? "secondary" : "outline-secondary"}
                            onClick={() => props.updateFilterOptions("type", PlotType.DayOfWeek)}
                        >
                            Day of Week
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
            <Row className="mt-2 mb-4">
                <Col md={12}>
                    <div className="metrics-control-title">Data Type</div>
                    <ButtonGroup size="sm" style={{marginRight: '4px'}} className="d-flex">
                        <Button
                            variant={props.filterOptions["scope"] === PlotScope.PointInTime ? "secondary" : "outline-secondary"}
                            onClick={() => props.updateFilterOptions("scope", PlotScope.PointInTime)}
                        >
                            Point-in-Time Status
                        </Button>
                        <Button
                            variant={props.filterOptions["scope"] === PlotScope.Actuals ? "secondary" : "outline-secondary"}
                            onClick={() => props.updateFilterOptions("scope", PlotScope.Actuals)}
                        >
                            Hourly Events
                        </Button>
                    </ButtonGroup>

                </Col>
            </Row>
            <Row className="mt-2 mb-4">
                <Col md={12}>
                    <div className="metrics-control-title">Dimensions</div>

                    <Form.Select className="d-flex" onChange={(e) => props.updateFilterOptions("dimension", e.target.value)}>
                        {
                            METRICS_DIMENSION_OPTIONS.map((selectOption: any, index: number) => {
                                return (
                                    <option key={selectOption.id} selected={props.filterOptions["dimension"] === index} value={index}>
                                        {selectOption.id === "ALL" ? "No Grouping" : selectOption.name}
                                    </option>
                                )
                            })
                        }
                    </Form.Select>

                </Col>
            </Row>
            <Row className="mt-2 mb-4">
                <Col md={12}>
                    <div className="metrics-control-title">Filters</div>
                    {/* Note when a group by is selected, users are not allowed to filter */}
                    <Form.Select className="d-flex" disabled={props.filterOptions["dimension"] > 0} value={props.filterOptions["dimension_filter_index"]} onChange={(e) => props.updateFilterOptions("dimension_filter_index", e.target.value)}>
                        {
                            METRICS_DIMENSION_OPTIONS.map((selectOption: any, index: number) => {
                                return (
                                    <option key={selectOption.id} value={index}>
                                        {selectOption.id === "ALL" ? "No Filters" : selectOption.name}
                                    </option>
                                )
                            })
                        }
                    </Form.Select>
                    <div className="mt-2">
                        {
                            props.filterOptions["dimension_filter_index"] > 0 && (
                                <>
                                    {
                                        METRICS_DIMENSION_VALUES[METRICS_DIMENSION_OPTIONS[props.filterOptions["dimension_filter_index"]].id].map((selectOption: any, index: number) => {
                                            return (
                                                <div className="d-flex" key={selectOption}>
                                                    <Form.Check
                                                        type={"checkbox"}
                                                        checked={true}
                                                        onChange={(e) => handleCheckFilter(e.target.checked, selectOption)}
                                                    >
                                                        <Form.Check.Input id={selectOption} type="checkbox" checked={props.filterOptions["dimension_filter_values"].includes(selectOption)} onChange={(e) => handleCheckFilter(e.target.checked, selectOption)} />
                                                        <Form.Check.Label htmlFor={selectOption}>{selectOption}</Form.Check.Label>
                                                    </Form.Check>
                                                </div>
                                            )
                                        })
                                    }
                                </>
                            )
                        }
                    </div>
                </Col>
            </Row>
            <Row className="mt-2 mb-4">
                <Col md={12}>
                    <div className="metrics-control-title">Period</div>

                    <Form.Select className="d-flex" onChange={(e) => props.updateFilterOptions("period", e.target.value)}>
                        {
                            METRICS_PERIOD_OPTIONS.map((selectOption: any, index: number) => {
                                return (
                                    <option key={selectOption.id} selected={props.filterOptions["period"] === index} value={index}>
                                        {selectOption.name}
                                    </option>
                                )
                            })
                        }
                    </Form.Select>
                </Col>
            </Row>
            <Row className="mt-2 mb-4">
                <Col md={12}>
                    <div className="metrics-control-title">Forecast Horizon</div>

                    <Form.Select className="d-flex" onChange={(e) => props.updateFilterOptions("forecast_horizon", e.target.value)}>
                        {
                            METRICS_FORECAST_HORIZON_OPTIONS.map((selectOption: any, index: number) => {
                                return (
                                    <option key={selectOption.id} selected={props.filterOptions["forecast_horizon"] === index} value={index}>
                                        {selectOption.name}
                                    </option>
                                )
                            })
                        }
                    </Form.Select>
                </Col>
            </Row>
            <Row className="mt-2 mb-4">
                <Col md={12}>
                    <div className="metrics-control-title">Display Options</div>

                    <div className="d-flex">
                        <Form.Check
                            type={"checkbox"}
                            checked={true}
                            onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_FORECAST", "display_options")}
                        >
                            <Form.Check.Input id={"showForecast"} type="checkbox" checked={props.filterOptions["display_options"].includes("SHOW_FORECAST")} onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_FORECAST", "display_options")} />
                            <Form.Check.Label htmlFor={"showForecast"}>Show Forecast</Form.Check.Label>
                        </Form.Check>
                    </div>
                    <div className="d-flex">
                        <Form.Check
                            type={"checkbox"}
                            checked={true}
                            onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_FORECAST_BOUNDS", "display_options")}
                        >
                            <Form.Check.Input id={"showForecastBounds"} type="checkbox" checked={props.filterOptions["display_options"].includes("SHOW_FORECAST_BOUNDS")} onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_FORECAST_BOUNDS", "display_options")} />
                            <Form.Check.Label htmlFor={"showForecastBounds"}>Show Forecast Bounds</Form.Check.Label>
                        </Form.Check>
                    </div>
                    <div className="d-flex">
                        <Form.Check
                            type={"checkbox"}
                            checked={true}
                            onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_LAST_WEEK", "display_options")}
                        >
                            <Form.Check.Input id={"showLastWeek"} type="checkbox" checked={props.filterOptions["display_options"].includes("SHOW_LAST_WEEK")} onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_LAST_WEEK", "display_options")} />
                            <Form.Check.Label htmlFor={"showLastWeek"}>Show Last Week</Form.Check.Label>
                        </Form.Check>
                    </div>
                    <div className="d-flex">
                        <Form.Check
                            type={"checkbox"}
                            checked={true}
                            onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_PAST_FORECAST", "display_options")}
                        >
                            <Form.Check.Input id={"showPastForecast"} type="checkbox" checked={props.filterOptions["display_options"].includes("SHOW_PAST_FORECAST")} onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_PAST_FORECAST", "display_options")} />
                            <Form.Check.Label htmlFor={"showPastForecast"}>Show Past Forecast</Form.Check.Label>
                        </Form.Check>
                    </div>
                    <div className="d-flex">
                        <Form.Check
                            type={"checkbox"}
                            checked={true}
                            onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_PAST_FORECAST_BOUNDS", "display_options")}
                        >
                            <Form.Check.Input id={"showPastForecastBounds"} type="checkbox" checked={props.filterOptions["display_options"].includes("SHOW_PAST_FORECAST_BOUNDS")} onChange={(e) => handleCheckFilter(e.target.checked, "SHOW_PAST_FORECAST_BOUNDS", "display_options")} />
                            <Form.Check.Label htmlFor={"showPastForecastBounds"}>Show Past Forecast Bounds</Form.Check.Label>
                        </Form.Check>
                    </div>
                </Col>
            </Row>
        </>
    );
};
